
import axios from "axios";
import {
  getTokenData,
  getRefreshToken,
  getUserData,
  setTokenData,
} from "./connectStore";
import { getAccessToken } from "../services/authService";
import { clearClientStorage } from "./sessionManager";
import { clearAuth } from "../redux/slices/authSlice";
import { setCurrentBusiness } from "../redux/slices/merchantSlice";

let authTokenRequest;
let forcedLogoutInProgress = false;

function getAuthToken() {
  if (!authTokenRequest) {
    authTokenRequest = makeActualAuthenticationRequest();
    authTokenRequest.then(resetAuthTokenRequest, resetAuthTokenRequest);
  }
  return authTokenRequest;
}

function resetAuthTokenRequest() {
  authTokenRequest = null;
}

const getStoreArtifacts = () => {
  try {
    // eslint-disable-next-line global-require
    const mod = require("../redux/store");
    return {
      store: mod?.store,
      persistor: mod?.persistor,
    };
  } catch (_) {
    return {
      store: null,
      persistor: null,
    };
  }
};

const shouldForceLogoutForRemovedAccess = (status, message = "") => {
  if (!status) return false;

  const normalizedMessage = String(message || "").toLowerCase();

  return (
    (status === 404 &&
      (normalizedMessage.includes("user not found") ||
        normalizedMessage.includes("no merchants found for this user"))) ||
    (status === 403 &&
      normalizedMessage.includes(
        "no merchant profiles found for your account",
      ))
  );
};

const forceLogoutForRemovedAccess = () => {
  if (forcedLogoutInProgress) return;
  forcedLogoutInProgress = true;

  const { store, persistor } = getStoreArtifacts();

  try {
    clearClientStorage();
    store?.dispatch?.(setCurrentBusiness(null));
    store?.dispatch?.(clearAuth());
    persistor?.purge?.();
  } catch (_) {
    // Best-effort cleanup only
  }

  if (typeof window !== "undefined") {
    const targetPath = "/";
    if (window.location.pathname !== targetPath) {
      window.location.assign(targetPath);
    } else {
      window.location.reload();
    }
  }
};

async function makeActualAuthenticationRequest() {
  try {
    const refresh = getRefreshToken();
    const res = await getAccessToken({ refreshToken: refresh });
    const { accessToken, refreshToken } = res;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return { accessToken, refreshToken };
  } catch (error) {
    throw new Error("Authentication failed");
  }
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const needsAuth = (url = "") =>
  url === "profile/updatePassword" ||
  url === "subscription/user" ||
  url === "subscription/createCheckoutSession" ||
  url.startsWith("subscription/") ||
  url.startsWith("subscription/non-renewedSubscription") ||
  url.startsWith("subscription/upcoming-renewals") ||
  url.startsWith("location") ||
  url.startsWith("location/merchant") ||
  url.startsWith("location/status") ||
  url.startsWith("subCategory") ||
  url.startsWith("fundraiser/funds") ||
  url.startsWith("payout") ||
  url === "auth/updateReferredBy" ||
  url === "fundraiser/updateContent" ||
  url.startsWith("ruleAndRegulation") ||
  url.startsWith("admin") ||
  url.startsWith("stats") ||
  url === "auth/logout" ||
  url === "notification" ||
  url.startsWith("notification") ||
  url.startsWith("assetAndPromotion") ||
  url.startsWith("offer") ||
  url.startsWith("category") ||
  url.startsWith("merchant/status") ||
  url.startsWith("fundraiser") ||
  url.startsWith("merchant/countOfFavoriteOffers") ||
  url.startsWith("merchant/getAboutPageView") ||
  url.startsWith("merchant/getOfferRedemptionByDemographics") ||
  url.startsWith("offerRedeem") ||
  url.startsWith("merchant") ||
  url.startsWith("promo") ||
  url.startsWith("waitlist/member") ||
  url.startsWith("waitlist/fundraiser") ||
  url.startsWith("homeFilter");

instance.interceptors.request.use(
  (config) => {
    config.headers["api-key"] = process.env.REACT_APP_API_KEY;
    if (needsAuth(config.url)) {
      const token = getTokenData();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  undefined,
  (err) => {
    const originalRequest = err?.config;
    const response = err?.response;

    if (response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      return getAuthToken()
        .then((resp) => {
          const userData = getUserData();
          setTokenData({
            ...userData,
            token: resp?.accessToken,
            refreshToken: resp?.refreshToken,
          });

          if (needsAuth(response?.config?.url)) {
            originalRequest.headers.Authorization = `Bearer ${resp?.accessToken}`;
          }

          return instance(originalRequest);
        })
        .catch((refreshError) => {
          forceLogoutForRemovedAccess();
          return Promise.reject(refreshError);
        });
    }

    // Normalize error so callers receive an Error instance with message
    const message =
      response?.data?.message ||
      response?.data?.error ||
      response?.statusText ||
      err?.message ||
      "Request failed";

    if (shouldForceLogoutForRemovedAccess(response?.status, message)) {
      forceLogoutForRemovedAccess();
    }

    if (response?.status === 401 && response?.config?.url === "auth/refresh") {
      forceLogoutForRemovedAccess();
    }

    const normalized = new Error(message);
    normalized.status = response?.status;
    normalized.data = response?.data;
    normalized.url = response?.config?.url;
    return Promise.reject(normalized);
  }
);

export default instance;
